import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { legalDocsApi, providerTermsApi } from '../services/api';
import { imageApi } from '../services/imageApi';

const DOC_TYPES = ['EULA', 'TERMS', 'PRIVACY', 'PROVIDER_TERMS'];
const LOCALES = ['en', 'he', 'es'];

const EMPTY_FORM = {
  type: 'TERMS',
  version: '',
  locale: 'en',
  region: '',
  title: '',
  url: '',
  requiresAcceptance: true,
};

const LegalDocuments = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await legalDocsApi.listAll();
      setDocs(data || []);
    } catch (e) {
      setError(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingDoc(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({
      type: doc.type,
      version: doc.version,
      locale: doc.locale,
      region: doc.region || '',
      title: doc.title,
      url: doc.url,
      requiresAcceptance: doc.requiresAcceptance,
    });
    setDialogOpen(true);
  };

  const [publishing, setPublishing] = useState(false);

  const publish = async (version) => {
    if (!window.confirm(
      `Publish Provider Terms ${version}?

`
      + 'This activates the contract platform-wide. Every existing provider will be blocked '
      + 'from managing services, store products and bookings until they accept. '
      + 'All required locales (he, en) must already be registered at this version.',
    )) return;
    setPublishing(true);
    try {
      const result = await providerTermsApi.publish(version);
      setError('');
      window.alert(`Published ${result.version} for: ${result.locales.join(', ')}`);
      await load();
    } catch (e) {
      setError(`Publish failed: ${e.body?.message || e.message}`);
    } finally {
      setPublishing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Provider Terms are global and always acceptance-bearing; the backend rejects anything
      // else. Normalize here so the UI can never send a value it has hidden.
      const isProviderTerms = form.type === 'PROVIDER_TERMS';
      const payload = {
        ...form,
        region: isProviderTerms ? null : (form.region.trim() || null),
        requiresAcceptance: isProviderTerms ? true : form.requiresAcceptance,
      };
      if (editingDoc) {
        await legalDocsApi.update(editingDoc.id, payload);
      } else {
        await legalDocsApi.create(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await legalDocsApi.delete(id);
      await load();
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const result = await imageApi.uploadDocument(file);
      if (result?.url) setForm((f) => ({ ...f, url: result.url }));
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Legal Documents</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Document
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage versioned legal documents (EULA, Terms, Privacy, Provider Terms) by locale and region.
        The mobile app fetches the latest doc per type and prompts users when versions change.
        Provider Terms are never shown to app users. They are global (no region) and always
        require acceptance. Register the document for BOTH he and en at the same non-draft
        version, then press Publish. Publishing activates the contract platform-wide and blocks
        every existing provider from managing services, store products and bookings until they
        accept.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Locale</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Req. Acceptance</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No documents yet. Create one above.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                docs.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Chip label={doc.type} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{doc.version}</TableCell>
                    <TableCell>{doc.locale}</TableCell>
                    <TableCell>{doc.region || '—'}</TableCell>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={doc.url}
                        size="small"
                        component="a"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        sx={{ maxWidth: 220 }}
                      />
                    </TableCell>
                    <TableCell>{doc.requiresAcceptance ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      {doc.type === 'PROVIDER_TERMS' && !doc.version.endsWith('-draft') && (
                        <Tooltip title="Publish this version platform-wide">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={publishing}
                              onClick={() => publish(doc.version)}
                              sx={{ mr: 1 }}
                            >
                              Publish
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(doc)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDoc ? 'Edit Document' : 'New Document'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Select value={form.type} onChange={setField('type')} size="small" displayEmpty>
              {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>

            <TextField label="Version (e.g. 1.1)" value={form.version}
              onChange={setField('version')} size="small" fullWidth />

            <Select value={form.locale} onChange={setField('locale')} size="small" displayEmpty>
              {LOCALES.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </Select>

            {form.type !== 'PROVIDER_TERMS' && (
              <TextField label="Region (optional, leave blank for all)" value={form.region}
                onChange={setField('region')} size="small" fullWidth />
            )}

            <TextField label="Title" value={form.title}
              onChange={setField('title')} size="small" fullWidth />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField label="URL" value={form.url}
                onChange={setField('url')} size="small" sx={{ flexGrow: 1 }} />
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.html,.htm"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || saving}
                size="small"
                sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
              >
                {uploading ? 'Uploading…' : 'Upload File'}
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.type === 'PROVIDER_TERMS' ? true : form.requiresAcceptance}
                  disabled={form.type === 'PROVIDER_TERMS'}
                  onChange={(e) => setForm((f) => ({ ...f, requiresAcceptance: e.target.checked }))}
                />
              }
              label={form.type === 'PROVIDER_TERMS'
                ? 'Requires acceptance (always on for Provider Terms)'
                : 'Requires user acceptance'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploading || !form.title || !form.url || !form.version}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegalDocuments;
